import React from 'react';
import { Box, Typography } from '@mui/material';

interface Props {
  resultPreviewUrls: string[];
  resultFiles: File[];
  inputRef: React.RefObject<HTMLInputElement | null>;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveResult: (index: number) => void;
}

const ResultsUploadZone: React.FC<Props> = ({ resultPreviewUrls, resultFiles, inputRef, onUpload, onRemoveResult }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const labelText = hoveredIndex !== null && resultFiles[hoveredIndex]
    ? resultFiles[hoveredIndex].name
    : 'Results';

  return (
  <Box
    sx={{
      position: 'relative',
      width: '637px',
      height: '396px',
      border: '2px dashed #959BEA',
      borderRadius: '30px',
      padding: resultPreviewUrls.length > 0 ? 0 : 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      cursor: resultFiles.length >= 4 ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
      '&:hover': {
        backgroundColor: resultPreviewUrls.length > 0 ? 'transparent' : '#f5f6fc',
      },
      '&:hover .hover-add-icon': {
        opacity: resultFiles.length > 0 && resultFiles.length < 4 ? 1 : 0,
      },


    }}
    component="label"
  >
    <input
      ref={inputRef}
      type="file"
      hidden
      multiple
      accept="video/*"
      onChange={onUpload}
      disabled={resultFiles.length >= 4}
    />

    {resultPreviewUrls.length > 0 ? (
      <>
        {/* Video grid */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'grid',
          gridTemplateColumns: resultPreviewUrls.length === 1 ? '1fr' : '1fr 1fr',
          gridTemplateRows: resultPreviewUrls.length <= 2 ? '1fr' : '1fr 1fr',
          gap: 0,
          zIndex: 1,
        }}>
          {resultPreviewUrls.map((url, index) => (
            <Box
              key={url}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              sx={{
                position: 'relative', width: '100%', height: '100%',
                gridColumn: resultPreviewUrls.length === 3 && index === 2 ? 'span 2' : 'auto',
                overflow: 'hidden',
                '&:hover .tile-overlay': { opacity: 1 },
                '&:hover .tile-remove-btn': { opacity: 1 },
              }}
            >
              <video src={url} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}>
                <track kind="captions" />
              </video>
              {/* Per-tile dark overlay */}
              <Box
                className="tile-overlay"
                sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.35)',
                  opacity: 0, transition: 'opacity 0.25s ease',
                  pointerEvents: 'none', zIndex: 5,
                }}
              />
              {/* Per-tile × button */}
              <Box
                className="tile-remove-btn"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveResult(index); }}
                sx={{
                  position: 'absolute', top: 8, right: 8, width: 24, height: 24,
                  borderRadius: '50%', backgroundColor: 'rgba(30,30,30,0.85)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', opacity: 0,
                  transition: 'opacity 0.2s ease, transform 0.2s ease',
                  zIndex: 10,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                  '&:hover': { transform: 'scale(1.1)', backgroundColor: 'rgba(0,0,0,0.9)' },
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </Box>

            </Box>
          ))}
        </Box>


        {/* Hover overlay: dim + plus icon */}
        <Box
          className="hover-gray-overlay"
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            opacity: 0, transition: 'opacity 0.3s ease',
            pointerEvents: 'none', borderRadius: '28px', zIndex: 2,
          }}
        />
        <Box
          className="hover-add-icon"
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'opacity 0.3s ease',
            pointerEvents: 'none', borderRadius: '28px', zIndex: 3,
          }}
        >
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%',
            backgroundColor: 'rgba(30,30,30,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3V17M3 10H17" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </Box>
        </Box>

        {/* Label overlay */}
        <Box
          sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            padding: '40px 16px 16px', borderRadius: '0 0 28px 28px', zIndex: 4,
          }}
        >
          <Typography variant="h5" sx={{ fontFamily: 'Maven Pro', fontWeight: 400, color: '#fff', fontSize: '20px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%', mx: 'auto', transition: 'all 0.2s ease' }}>
            {labelText}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#fff', fontSize: '14px', textAlign: 'center' }}>
            {resultFiles.length} video(s) selected
          </Typography>
        </Box>
      </>
    ) : (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
          <Box sx={{ width: '62px', height: '55px', borderLeft: '40px solid transparent', borderRight: '40px solid transparent', borderBottom: '35px solid #959BEA' }} />
          <Box sx={{ width: '35px', height: '25px', backgroundColor: '#959BEA', mb: '5px' }} />
          <Box sx={{ width: '80px', height: '11.5px', backgroundColor: '#959BEA', borderRadius: '2px' }} />
        </Box>
        <Typography variant="h5" sx={{ fontFamily: 'Maven Pro', fontWeight: 400, color: '#000', fontSize: '20px', mt: 1 }}>
          Results
        </Typography>
      </>
    )}
  </Box>
  );
};

export default ResultsUploadZone;
